import { useRef, useState } from 'react';
import ReactInputMask from 'react-input-mask';
import { Pencil, Save, X } from 'lucide-react';

import { Button } from 'components/ui/button';
import type { YardGateAppointmentStatus } from 'contracts/YardGate';
import {
  formatYardGatePhone,
  yardGatePhoneDigits,
  type YardGateEntry,
} from '../../adapter';

const PHONE_MASK = '(99) 99999-9999';

const editableStatuses = new Set<YardGateAppointmentStatus>([
  'PENDING',
  'AWAITING_RELEASE',
]);

interface PhoneCellProps {
  entry: YardGateEntry;
  isBusy: boolean;
  onSave: (phone: string | null) => Promise<boolean>;
}

export const PhoneCell = ({ entry, isBusy, onSave }: PhoneCellProps) => {
  const initialValue = formatYardGatePhone(entry.phone);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState('');
  const previousValue = useRef(initialValue);
  const canEdit = editableStatuses.has(entry.status);

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="truncate">
          {entry.phone ? formatYardGatePhone(entry.phone) : '—'}
        </span>
        {canEdit ? (
          <button
            type="button"
            aria-label={`Editar Telefone de ${entry.plate}`}
            title="Editar Telefone"
            disabled={isBusy}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:opacity-50"
            onClick={() => {
              const formatted = formatYardGatePhone(entry.phone);
              previousValue.current = formatted;
              setValue(formatted);
              setError('');
              setEditing(true);
            }}
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
        ) : null}
      </span>
    );
  }

  const save = async () => {
    if (isBusy) return;
    const digits = yardGatePhoneDigits(value);
    if (digits && ![10, 11].includes(digits.length)) {
      setError('Informe um telefone com 10 ou 11 dígitos.');
      return;
    }
    const saved = await onSave(digits || null);
    if (saved) setEditing(false);
  };

  return (
    <div className="inline-flex min-w-[240px] items-center gap-1">
      <div className="flex flex-col">
        <ReactInputMask
          id={`phone-${entry.id}`}
          aria-label={`Telefone de ${entry.plate}`}
          aria-describedby={error ? `phone-${entry.id}-error` : undefined}
          mask={PHONE_MASK}
          inputMode="tel"
          disabled={isBusy}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError('');
          }}
          className="h-9 w-[150px] rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-[#EE7C2C] focus:ring-1 focus:ring-[#EE7C2C] disabled:opacity-50"
        />
        {error ? (
          <span id={`phone-${entry.id}-error`} className="text-xs text-red-600">
            {error}
          </span>
        ) : null}
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-label="Salvar telefone"
        title="Salvar Telefone"
        onClick={save}
        disabled={isBusy}
      >
        <Save size={16} aria-hidden="true" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        aria-label="Cancelar edição do telefone"
        title="Cancelar edição do Telefone"
        onClick={() => {
          setValue(previousValue.current);
          setError('');
          setEditing(false);
        }}
        disabled={isBusy}
      >
        <X size={16} aria-hidden="true" />
      </Button>
    </div>
  );
};
